import React, { useState, useEffect } from "react";
import { doc, updateDoc } from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import { db } from "../../../firebaseConfig";

function ArticleForm({
  volumeId,
  issue,
  article,
  articleIndex,
  onClose,
  onSave,
}) {
  const [title, setTitle] = useState("");
  const [authors, setAuthors] = useState([""]);
  const [position, setPosition] = useState("");
  const [institution, setInstitution] = useState("");
  const [abstract, setAbstract] = useState("");
  const [order, setOrder] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [existingPdfUrl, setExistingPdfUrl] = useState("");

  const isEditing = article !== null;

  useEffect(() => {
    if (isEditing) {
      setTitle(article.title);
      setAuthors(article.authors);
      setPosition(article.position);
      setInstitution(article.institution);
      setAbstract(article.abstract);
      setOrder(article.order);
      setExistingPdfUrl(article.pdfUrl); // Store existing URL for display
    }
  }, [article, isEditing]);

  const handleAuthorChange = (index, value) => {
    const newAuthors = [...authors];
    newAuthors[index] = value;
    setAuthors(newAuthors);
  };

  const addAuthor = () => {
    setAuthors([...authors, ""]);
  };

  const removeAuthor = (index) => {
    if (authors.length > 1) {
      const newAuthors = authors.filter((_, i) => i !== index);
      setAuthors(newAuthors);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditing && !file) {
      setError("A PDF file is required for new articles.");
      return;
    }
    setLoading(true);
    setError("");
    setUploadProgress(0);

    try {
      let pdfUrl = isEditing ? article.pdfUrl : "";
      let fileName = isEditing ? article.fileName : "";

      if (file) {
        const storage = getStorage();
        fileName = `${Date.now()}-${file.name}`;
        const storageRef = ref(storage, `journal_articles/${fileName}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(Math.round(progress));
            },
            (error) => {
              console.error("Upload failed:", error);
              setError("File upload failed. Please try again.");
              setLoading(false);
              reject(error);
            },
            async () => {
              pdfUrl = await getDownloadURL(uploadTask.snapshot.ref);
              resolve();
            },
          );
        });
      }

      const articleData = {
        articleId: isEditing ? article.articleId : Date.now().toString(), // Use existing ID or generate new
        title,
        authors,
        position,
        institution,
        abstract,
        order: parseInt(order),
        pdfUrl,
        fileName,
      };
      const updatedArticles = [...(issue.articles || [])];
      if (isEditing) {
        updatedArticles[articleIndex] = articleData;
      } else {
        updatedArticles.push(articleData);
      }

      const issueRef = doc(db, `journalVolumes/${volumeId}/issues`, issue.id);
      await updateDoc(issueRef, { articles: updatedArticles });

      onSave();
      onClose();
    } catch (err) {
      console.error("Error saving article:", err);
      setError("Failed to save article. Please check console for details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl">
        <h3 className="text-lg font-bold mb-4 text-slate-800">
          {isEditing ? "Edit Article" : "Add New Article"}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="articleTitle"
              className="block text-sm font-medium text-gray-700"
            >
              Title
            </label>
            <input
              type="text"
              id="articleTitle"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Author(s)
            </label>
            {authors.map((author, index) => (
              <div key={index} className="flex items-center gap-2 mt-1">
                <input
                  type="text"
                  value={author}
                  onChange={(e) => handleAuthorChange(index, e.target.value)}
                  className="block w-full border border-gray-300 rounded-md shadow-sm p-2"
                  placeholder={`Author ${index + 1}`}
                  required
                />
                {authors.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeAuthor(index)}
                    className="text-red-500 hover:text-red-700 p-1 rounded-full"
                  >
                    &times;
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addAuthor}
              className="mt-2 text-sm font-medium text-sky-600 hover:text-sky-800"
            >
              + Add Author
            </button>
          </div>

          <div>
            <label
              htmlFor="position"
              className="block text-sm font-medium text-gray-700"
            >
              Position
            </label>
            <input
              id="position"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              rows="6"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
              required
            />
          </div>

          <div>
            <label
              htmlFor="institution"
              className="block text-sm font-medium text-gray-700"
            >
              Institution
            </label>
            <input
              id="institution"
              value={institution}
              onChange={(e) => setInstitution(e.target.value)}
              rows="6"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
              required
            />
          </div>
          <div>
            <label
              htmlFor="abstract"
              className="block text-sm font-medium text-gray-700"
            >
              Abstract
            </label>
            <textarea
              id="abstract"
              value={abstract}
              onChange={(e) => setAbstract(e.target.value)}
              rows="6"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
              required
            />
          </div>

          <div>
            <label
              htmlFor="file"
              className="block text-sm font-medium text-gray-700"
            >
              Article PDF
            </label>
            <input
              type="file"
              id="file"
              onChange={handleFileChange}
              accept=".pdf"
              className="mt-1 block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-sky-50 file:text-sky-700 hover:file:bg-sky-100"
            />
            {isEditing && (
              <p className="text-xs text-slate-500 mt-1">
                Leave blank to keep the existing file.
              </p>
            )}
          </div>

          {loading && uploadProgress > 0 && (
            <div className="w-full bg-gray-200 rounded-full">
              <div
                className="bg-sky-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full"
                style={{ width: `${uploadProgress}%` }}
              >
                {uploadProgress}%
              </div>
            </div>
          )}

          {error && <p className="text-red-500 text-sm">{error}</p>}

          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-sky-600 text-white rounded-md text-sm font-medium hover:bg-sky-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Article"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ArticleForm;
